import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import CreateDamage from "../../damages/create-damage/CreateDamagePage";
import { DamagePropertiesStorageModel } from "../../../services/damage/DamagePropertiesStorage";
import { CurrentInspectionStorageModel } from "../../../services/inspection/CurrentInspectionStorage";
import { CurrentSectionStorageModel } from "../../../services/sewer-object/CurrentSectionStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import {
  SectionMenuItemsModel,
  SectionDynamicMenuItemsPages,
} from "../../sewer-objects/section-details/SectionMenuItems";
import PageLayout from "../../_layout/PageLayout";
import { api } from "../../../services/api/ApiService";
import { CreateDamageInAssignedProject, CreateDamage as CreateDamagePermission } from "../../../services/authorization/Permissions";
import useRouteService from "../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";

const CreateDamageFromAnalysisPage = observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();

  const currentSectionId = urlParamsService.getSectionId()!;
  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create({
      currentSectionId: currentSectionId,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [damagePropertiesStorage] = useState(() =>
    DamagePropertiesStorageModel.create({ sewerObjectId: currentSectionId }, { api })
  );

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  useEffect(() => {
    if (
      !currentInspectionStorage.isLoading &&
      !currentInspectionStorage.currentInspection.hasVideo
    ) {
      history.push("/404");
    }
  }, [currentInspectionStorage.currentInspection, currentInspectionStorage.isLoading, history]);

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      inspectionId: currentInspectionId,
      dynamicMenuItemPage: SectionDynamicMenuItemsPages.CreateDamageFromAnalysis,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [currentSectionStorage.currentSectionId, currentSectionStorage.currentSection, currentSectionStorage.parentEntityFromPath.id, currentInspectionId]);

  const getInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentSectionStorage.parentEntityFromPath.type,
        name: await currentSectionStorage.getParentEntityName(currentSectionStorage.currentSection.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentSectionStorage.currentSection.name
        }, {
          entity: EntityTypeFromPath.Inspection,
          name: getInspectionName()
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentSectionStorage, currentSectionStorage.currentSection, getInspectionName, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[CreateDamagePermission, CreateDamageInAssignedProject]}
      titleKey="CreateDamage.add"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getInspectionName(),
      }}
      id="create-damage"
      menuItems={menuItems}
      isLoading={currentSectionStorage.isLoading}
    >
      <CreateDamage
        currentSectionStorage={currentSectionStorage}
        damagePropertiesStorageModel={damagePropertiesStorage}
        currentInspectionStorage={currentInspectionStorage}
      />
    </PageLayout>
  );
});

export default CreateDamageFromAnalysisPage;
